import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { User, Bell, ChevronDown, Shield, Briefcase, Building2 } from 'lucide-react';
import { authAPI } from '../services/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

import { toast } from '../hooks/use-toast';

const Header = () => {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState(null);
  const [userFullName, setUserFullName] = useState(null);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const email = localStorage.getItem('userEmail');
    const fullName = localStorage.getItem('userFullName');
    const role = localStorage.getItem('userRole');
    setUserEmail(email);
    setUserFullName(fullName);
    setUserRole(role);
  }, []);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      setUserEmail(null);
      setUserFullName(null);
      setUserRole(null);
      toast({
        title: 'Success',
        description: 'Logged out successfully',
      });
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if API call fails
      setUserEmail(null);
      setUserFullName(null);
      setUserRole(null);
      navigate('/');
    }
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-6 py-3 border-b">
          <Link to="/" className="flex items-center">
            <span className="text-3xl font-bold text-[#4a90e2]" style={{ fontFamily: 'Arial, sans-serif' }}>
              JobGenie<span className="text-gray-700">.in</span>
            </span>
          </Link>

          <div className="flex items-center gap-6">
            <Button variant="ghost" className="text-gray-700 hover:text-[#4a90e2] transition-colors">
              <Bell className="w-5 h-5 mr-2" />
              Notifications
            </Button>
            
            {userEmail ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 px-4 py-2 bg-[#4a90e2] text-white rounded-md hover:bg-[#3a7bc8] transition-colors">
                  <User className="w-5 h-5" />
                  <span>{userFullName || 'My Account'}</span>
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {userRole === 'job_seeker' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate(`/profile/${userEmail}`)}>
                        View Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/edit-profile/${userEmail}`)}>
                        Edit Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/my-applications')}>
                        <Briefcase className="w-4 h-4 mr-2" />
                        My Applications
                      </DropdownMenuItem>
                    </>
                  )}
                  {userRole === 'recruiter' && (
                    <DropdownMenuItem onClick={() => navigate('/recruiter/dashboard')}>
                      <Briefcase className="w-4 h-4 mr-2" />
                      Recruiter Dashboard
                    </DropdownMenuItem>
                  )}
                  {userRole === 'super_admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/dashboard')}>
                      <Shield className="w-4 h-4 mr-2" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  {userRole === 'company_hr' && (
                    <DropdownMenuItem onClick={() => navigate('/hr/dashboard')}>
                      <Building2 className="w-4 h-4 mr-2" />
                      HR Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button className="bg-[#4a90e2] hover:bg-[#3a7bc8] text-white transition-colors" data-testid="header-login-btn">
                    <User className="w-5 h-5 mr-2" />
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button variant="outline" className="border-[#4a90e2] text-[#4a90e2] hover:bg-[#4a90e2] hover:text-white transition-colors" data-testid="header-register-btn">
                    Register
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Navigation Bar */}
        <nav className="px-6 py-3">
          <ul className="flex items-center gap-8">
            <li>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center text-gray-700 hover:text-[#4a90e2] transition-colors cursor-pointer font-medium">
                  Jobs <ChevronDown className="w-4 h-4 ml-1" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem>Search Jobs</DropdownMenuItem>
                  <DropdownMenuItem>Browse Jobs</DropdownMenuItem>
                  <DropdownMenuItem>Jobs by Skill</DropdownMenuItem>
                  <DropdownMenuItem>Jobs by Location</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
            <li>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center text-gray-700 hover:text-[#4a90e2] transition-colors cursor-pointer font-medium">
                  Companies <ChevronDown className="w-4 h-4 ml-1" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem>Browse Companies</DropdownMenuItem>
                  <DropdownMenuItem>Top Companies</DropdownMenuItem>
                  <DropdownMenuItem>Company Reviews</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
            <li>
              <Link to="/services" className="text-gray-700 hover:text-[#4a90e2] transition-colors font-medium">
                Services
              </Link>
            </li>
            <li>
              <Link to="/resume" className="text-gray-700 hover:text-[#4a90e2] transition-colors font-medium">
                Resume Writing
              </Link>
            </li>
            <li>
              <Link to="/learning" className="text-gray-700 hover:text-[#4a90e2] transition-colors font-medium">
                Learning
              </Link>
            </li>
            <li>
              <Link to="/career-advice" className="text-gray-700 hover:text-[#4a90e2] transition-colors font-medium">
                Career Advice
              </Link>
            </li>
            <li className="ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center text-gray-500 hover:text-[#4a90e2] transition-colors cursor-pointer text-sm">
                  For Employers <ChevronDown className="w-4 h-4 ml-1" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => navigate('/recruiter/login')}>
                    <Briefcase className="w-4 h-4 mr-2" />
                    Recruiter Login
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/hr/login')}>
                    <Building2 className="w-4 h-4 mr-2" />
                    Company HR Login
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/login')}>
                    <Shield className="w-4 h-4 mr-2" />
                    Admin Login
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
